import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, Slider, Chip, Menu, MenuItem, InputAdornment, useMediaQuery, useTheme, Divider, Select, FormControl, InputLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import Tips from './dialogs/Tips';
import LifeStageSelector from './LifeStageSelector';
import StyledTextField from './common/StyledTextField';
import { Platform } from '../types/Game';
import { GameReviewData } from '../types/Review';
import { setGameReview, getPlatforms, getGamePlatforms, getUserBirthDate, getGameMemo, saveGameMemo, getGamePlaytimes, saveGamePlaytime, deleteGamePlaytime } from '../services/api';
import ReviewPreviewDialog from './dialogs/ReviewPreviewDialog';
import { scoreTags } from '../utils/scoreTags';

interface GameReviewDialogProps {
  open: boolean;
  onClose: () => void;
  gameId: number;
  initialData: GameReviewData;
  platforms: Platform[];
  releaseDate?: string;
  userBirthDate?: string;
  onReviewSaved?: (reviewData: GameReviewData) => void;
}

interface PlaytimeData {
  id: number;
  playstyle: number;
  play_time_minutes: number;
  note: string;
}

const PLAYSTYLE_LABELS: { [key: number]: string } = {
  0: 'フリー',
  1: 'メイン',
  2: 'メイン + サブ',
  3: 'コンプリート'
};

const GameReviewDialog: React.FC<GameReviewDialogProps> = ({
  open,
  onClose,
  gameId,
  initialData,
  platforms: initialPlatforms,
  releaseDate,
  userBirthDate: initialBirthDate,
  onReviewSaved
}) => {
  const [tempData, setTempData] = useState<GameReviewData>(initialData);
  const [availablePlatforms, setAvailablePlatforms] = useState<Platform[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>(initialPlatforms);
  const [userBirthDate, setUserBirthDate] = useState<string | null>(initialBirthDate || null);
  const [isDialogDirty, setIsDialogDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [memo, setMemo] = useState<string>('');
  const [initialMemo, setInitialMemo] = useState<string>('');
  const [playtimeRecords, setPlaytimeRecords] = useState<PlaytimeData[]>([]);
  const [selectedPlaystyle, setSelectedPlaystyle] = useState<number>(1);
  const [playtimeHours, setPlaytimeHours] = useState<string>('');
  const [playtimeMinutes, setPlaytimeMinutes] = useState<string>('');
  const [playtimeNote, setPlaytimeNote] = useState<string>('');
  const [initialPlaytimeData, setInitialPlaytimeData] = useState<{ hours: string; minutes: string; note: string }>({ hours: '', minutes: '', note: '' });

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        const [availablePlatformsData, gamePlatforms, birthDate, memoData, playtimeData] = await Promise.all([
          getPlatforms(),
          !initialPlatforms.length ? getGamePlatforms(gameId) : Promise.resolve(initialPlatforms),
          !initialBirthDate ? getUserBirthDate() : Promise.resolve(initialBirthDate),
          getGameMemo(gameId).catch(() => ({ data: { memo: '' } })),
          getGamePlaytimes(gameId).catch(() => ({ data: [] }))
        ]);

        setAvailablePlatforms(availablePlatformsData);
        setPlatforms(gamePlatforms);
        setUserBirthDate(birthDate);
        const memoText = memoData.data.memo || '';
        setPlaytimeRecords(playtimeData.data);
        setMemo(memoText);
        setInitialMemo(memoText);

        setTempData(prev => ({
          ...prev,
          platform: prev.platform.length > 0
            ? prev.platform
            : (gamePlatforms.length === 1 ? [gamePlatforms[0].platform_id] : [])
        }));
      };

      fetchData();
    }
  }, [open, initialData, initialPlatforms, initialBirthDate, gameId]);

  // プレイスタイル変更時のハンドラー
  const handlePlaystyleChange = (newPlaystyle: number) => {
    setSelectedPlaystyle(newPlaystyle);
    const existingRecord = playtimeRecords.find(r => r.playstyle === newPlaystyle);
    if (existingRecord) {
      const hours = Math.floor(existingRecord.play_time_minutes / 60);
      const minutes = existingRecord.play_time_minutes % 60;
      const hoursStr = hours.toString();
      const minutesStr = minutes.toString();
      const noteStr = existingRecord.note;
      setPlaytimeHours(hoursStr);
      setPlaytimeMinutes(minutesStr);
      setPlaytimeNote(noteStr);
      setInitialPlaytimeData({ hours: hoursStr, minutes: minutesStr, note: noteStr });
    } else {
      setPlaytimeHours('');
      setPlaytimeMinutes('');
      setPlaytimeNote('');
      setInitialPlaytimeData({ hours: '', minutes: '', note: '' });
    }
  };

  // プレイタイム保存ハンドラー
  const handleSavePlaytime = async () => {
    try {
      const hours = parseInt(playtimeHours) || 0;
      const minutes = parseInt(playtimeMinutes) || 0;
      const totalMinutes = hours * 60 + minutes;

      if (totalMinutes === 0) {
        alert('プレイ時間を入力してください');
        return;
      }

      // フリースタイル（0）の場合はノート必須
      if (selectedPlaystyle === 0 && !playtimeNote.trim()) {
        alert('フリースタイルではノートの入力が必須です。\nどのようなプレイ内容だったか詳細を記入してください。');
        return;
      }

      // saveGamePlaytimeはPOSTでupsert（作成or更新）に対応
      await saveGamePlaytime(gameId, selectedPlaystyle, totalMinutes, playtimeNote);

      // データを再取得
      const playtimeData = await getGamePlaytimes(gameId);
      setPlaytimeRecords(playtimeData.data);
    } catch (error) {
      console.error('プレイタイムの保存に失敗しました', error);
    }
  };

  // プレイタイム削除ハンドラー
  const handleDeletePlaytime = async () => {
    try {
      const existingRecord = playtimeRecords.find(r => r.playstyle === selectedPlaystyle);
      if (!existingRecord) return;

      if (window.confirm('このプレイタイム記録を削除しますか？')) {
        await deleteGamePlaytime(gameId, selectedPlaystyle);
        setPlaytimeRecords(playtimeRecords.filter(r => r.playstyle !== selectedPlaystyle));
        setPlaytimeHours('');
        setPlaytimeMinutes('');
        setPlaytimeNote('');
      }
    } catch (error) {
      console.error('プレイタイムの削除に失敗しました', error);
    }
  };

  const handleSave = async () => {
    try {
      // レビューデータが変更されているかチェック（JSON比較で簡潔に）
      const isReviewChanged = JSON.stringify(tempData) !== JSON.stringify(initialData);

      // メモが変更されているかチェック
      const isMemoChanged = memo !== initialMemo;

      // プレイタイムが変更されているかチェック
      const isPlaytimeChanged =
        playtimeHours !== initialPlaytimeData.hours ||
        playtimeMinutes !== initialPlaytimeData.minutes ||
        playtimeNote !== initialPlaytimeData.note;

      const promises = [];

      // レビューデータが変更されている場合のみレビューを更新
      if (isReviewChanged) {
        promises.push(
          setGameReview(
            gameId,
            tempData.score,
            tempData.reviewText,
            tempData.playStartDate,
            tempData.playEndDate,
            tempData.playTime,
            tempData.scoreTags,
            tempData.platform,
            tempData.scoreVisual,
            tempData.scoreSound,
            tempData.scoreGameplay,
            tempData.scoreNarrative,
            tempData.scoreOriginality
          )
        );
      }

      // メモが変更されている場合のみメモを更新
      if (isMemoChanged) {
        promises.push(saveGameMemo(gameId, memo));
      }

      // プレイタイムが変更されている場合のみ保存
      if (isPlaytimeChanged) {
        const hours = parseInt(playtimeHours) || 0;
        const minutes = parseInt(playtimeMinutes) || 0;
        const totalMinutes = hours * 60 + minutes;

        // プレイ時間が入力されている場合のみ保存
        if (totalMinutes > 0) {
          // フリースタイルの場合はノート必須チェック
          if (selectedPlaystyle === 0 && !playtimeNote.trim()) {
            alert('フリースタイルではノートの入力が必須です。\nどのようなプレイ内容だったか詳細を記入してください。');
            return;
          }
          promises.push(saveGamePlaytime(gameId, selectedPlaystyle, totalMinutes, playtimeNote));
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      onReviewSaved?.(tempData);
      onClose();
    } catch (error) {
      console.error('レビューの保存に失敗しました', error);
    }
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [platformMenuAnchorEl, setPlatformMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [platformSearch, setPlatformSearch] = React.useState('');

  const scoreMarks = [
    { value: 1, label: '1.0' },
    { value: 2, label: 'poor' },
    { value: 3, label: '3.0' },
    { value: 4, label: 'good' },
    { value: 5, label: '5.0' },
  ];

  const detailScoreMarks = [
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
  ];

  const handleScoreChange = (event: Event, newValue: number | number[]) => {
    setTempData({ ...tempData, score: newValue as number });
    setIsDialogDirty(true);
  };

  const handleDetailScoreChange = (field: keyof GameReviewData, event: Event, newValue: number | number[]) => {
    setTempData({ ...tempData, [field]: newValue as number });
    setIsDialogDirty(true);
  };

  const handleScoreTagsChange = (tagId: number) => {
    const newTempScoreTags = tempData.scoreTags.includes(tagId)
      ? tempData.scoreTags.filter((id) => id !== tagId).sort((a, b) => a - b)
      : tempData.scoreTags.length >= 3
        ? tempData.scoreTags
        : [...tempData.scoreTags, tagId].sort((a, b) => a - b);
    setTempData({ ...tempData, scoreTags: newTempScoreTags });
    setIsDialogDirty(true);
  };

  const handlePlatformChange = (platformId: number) => {
    const newPlatform = tempData.platform.includes(platformId)
      ? tempData.platform.filter(id => id !== platformId).sort((a, b) => a - b)
      : tempData.platform.length >= 2
        ? tempData.platform
        : [...tempData.platform, platformId].sort((a, b) => a - b);
    
    setTempData({ ...tempData, platform: newPlatform });
    setIsDialogDirty(true);
  };

  const handlePlatformMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPlatformMenuAnchorEl(event.currentTarget);
  };

  const handlePlatformMenuClose = () => {
    setPlatformMenuAnchorEl(null);
    setPlatformSearch('');
  };

  const handlePlatformSelect = (platformId: number) => {
    handlePlatformChange(platformId);
    handlePlatformMenuClose();
  };

  const filteredPlatforms = availablePlatforms
    .filter(p => !platforms.some(defaultP => defaultP.platform_id === p.platform_id))
    .filter(p => 
      p.name.toLowerCase().includes(platformSearch.toLowerCase())
    );

  const getScoreText = (score: number | null | undefined) => {
    if (score === null || score === undefined) {
      return 'N/A';
    }
    const numericScore = typeof score === 'string' ? parseFloat(score) : score;
    return isNaN(numericScore) ? '未評価' : numericScore.toFixed(1);
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return "#666666";
    if (score >= 4.5) return "#4CAF50";
    if (score >= 4.0) return "#8BC34A";
    if (score >= 3.5) return "#CDDC39";
    if (score >= 3.0) return "#FFEB3B";
    if (score >= 2.5) return "#FFC107";
    if (score >= 2.1) return "#FF9800";
    return "#F44336";
  };

  const getDetailScoreStyle = (score: number | null | undefined) => {
    const hasScore = score !== null && score !== undefined;
    return {
      '& .MuiSlider-thumb': {
        width: 12,
        height: 12,
        color: hasScore ? 'primary.main' : 'grey.500',
      },
      '& .MuiSlider-track': {
        color: hasScore ? 'primary.main' : 'grey.500',
      },
      '& .MuiSlider-rail': {
        color: hasScore ? 'primary.light' : 'grey.300',
      },
    };
  };

  const reviewCorrectionTips = (
    <Box>
      <Typography variant="body2" paragraph>
        ・スコア補正とは、あなたの評価に大きく影響を与えている可能性のある要因を示すものです。
      </Typography>
      <Typography variant="body2" paragraph>
        ・確信がなくても構いません。「もしかしたら」という程度の直感も大切です。
      </Typography>
      <Typography variant="body2" paragraph>
        ・自分では気づきにくいバイアスもあります。他ユーザーとの評価に乖離を感じる状況などで自問し、思い当たる点があれば選択してください。
      </Typography>
      <Typography variant="body2" paragraph>
        ・これはスコアの妥当性を示すものではなく、あなたの評価の背景やゲーム体験を他のユーザーに表明するためのものです。
      </Typography>
      <Typography variant="body2" paragraph>
        ・正直に、かつ慎重に選択することで、あなたのレビューの信頼性と他のユーザーの理解が深まります。
      </Typography>
      <Typography variant="body2" paragraph>
        ・全てのレビューで選択する必要はありません。スコアに大きく関与している可能性がある場合でのみ設定してください。
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Chip
            label="Dev / Pub補正"
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2">
            デベロッパーやパブリッシャーに関する評価要因です。
            企業への愛着や、実績・評判・方針などがゲーム内容とは別に評価を左右する可能性を考慮します。
          </Typography>
        </Box>
        <Box>
          <Chip
            label="コンテクスト補正"
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2">
            ゲームを取り巻く背景や状況を考慮に入れた評価要因です。
            個人的な嗜好や体験とは異なり、より広い文脈でゲームの価値を評価する際に使用します。
          </Typography>
        </Box>
        <Box>
          <Chip
            label="エントリー補正"
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2">
            あなたにとって新しい、または不慣れなゲーム要素が評価に影響を与えている可能性を示すものです。初めて遊ぶジャンルやシリーズでは、新鮮さや驚きによる高評価や、操作や概念への不慣れによる低評価など、経験値の差が評価に影響する傾向があります。
          </Typography>
        </Box>
        <Box>
          <Chip
            label="期待値補正"
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2">
            事前の期待値と実際の体験のギャップが評価に影響している可能性を示します。
            高い期待からの失望や、予想以上の完成度への驚きなど、期待と実体験の落差が評価に反映されることがあります。
          </Typography>
        </Box>
      </Box>  
    </Box>
  );

  const reviewFormatTips = (
    <Box>
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          mb: 0.5,
          color: 'text.secondary',
          fontStyle: 'italic',
          lineHeight: 1.2
        }}
      >
        Tips：コードをクリックでクリップボードにコピーできます
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          {
            title: '見出し',
            examples: [
              { syntax: '[h1]ヘッダー[/h1]', result: 'ヘッダー' },
              { syntax: '[h2]ヘッダー[/h2]', result: 'ヘッダー' },
              // { syntax: '[h3]小見出し[/h3]', result: '小見出し' }
            ]
          },
          {
            title: 'テキスト装飾',
            examples: [
              { syntax: '[b]太字[/b]', result: '太字' },
              { syntax: '[i]斜体[/i]', result: '斜体' },
              { syntax: '[s]取り消し線[/s]', result: '取り消し線' },
              { syntax: '[u]下線[/u]', result: '下線' }
            ]
          },
          {
            title: 'リスト',
            examples: [
              { syntax: '[list]\n[*]リスト項目\n[*]リスト項目\n[/list]', result: '箇条書き' },
              { syntax: '[olist]\n[*]リスト項目\n[*]リスト項目\n[/olist]', result: '番号付きリスト' }
            ]
          },
          {
            title: 'その他',
            examples: [
              { syntax: '[quote]引用文[/quote]', result: '引用ブロック' },
              { syntax: '[spoiler]ネタバレ[/spoiler]', result: 'ネタバレ' },
            ]
          }
        ].map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              {section.title}
            </Typography>
            {section.examples.map((example, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1,
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                  }
                }}
              >
                <Box
                  component="code"
                  onClick={() => navigator.clipboard.writeText(example.syntax)}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    p: 0.5,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                    }
                  }}
                >
                  {example.syntax}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {example.result}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog 
        open={open} 
        onClose={() => onClose()}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle>ゲームステータス</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            {/* 総合スコア */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ lineHeight: 1 }}>
                  スコア
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontSize: "0.9rem",
                      fontWeight: '600',
                      py: 0,
                      px: 1.25,
                      borderRadius: 1.5,
                      backgroundColor: `${getScoreColor(tempData.score)}B3`,
                      textAlign: 'center'
                    }}
                  >
                    {getScoreText(tempData.score)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }}>
                    基準値：3.0=普通
                  </Typography>
                </Box>
              </Box>
              <Slider
                value={tempData.score ?? 3}
                onChange={handleScoreChange}
                aria-labelledby="game-score-slider"
                step={0.1}
                marks={scoreMarks}
                min={1}
                max={5}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.main' : 'grey.500',
                    width: 18,
                    height: 18,
                  },
                  '& .MuiSlider-track': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.main' : 'grey.500',
                    height: 3,
                  },
                  '& .MuiSlider-rail': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.light' : 'grey.300',
                    height: 3,
                  },
                }}
              />
            </Box>

            {/* 詳細スコア */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography>
                  評価項目
                </Typography>
                <Tips 
                  title="Pulse Rating Standards" 
                  maxWidth="sm"
                  buttonComponent={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HelpOutlineIcon 
                        fontSize="small" 
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }} 
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          cursor: 'pointer',
                          color: 'rgba(255, 255, 255, 0.7)',
                          lineHeight: 1,
                          '&:hover': { 
                            textDecoration: 'underline',
                            color: 'rgba(255, 255, 255, 0.9)'
                          }
                        }}
                      >
                        スコアガイドを見る
                      </Typography>
                    </Box>
                  }
                  content={
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        ビジュアル
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・アートスタイル（美術、デザイン全般）
・グラフィック品質（描画技術、フレームレート、解像度）
・視覚演出（エフェクト、ライティング、カメラワーク）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        ゲームの見た目に関する全ての要素を評価します。最新の3Dグラフィックだけでなく、ピクセルアートやフォトリアルなど、そのゲームが目指すアートスタイルがどれだけ効果的に表現されているかが重要です。また、リリース時の技術水準を最も考慮すべき項目でもあります。
                      </Typography>
                      
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        サウンド
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・音楽（BGM、楽曲の質・印象に残るか）
・効果音（SE、環境音）
・音声（ボイス演技、音声の品質）
・音響演出（音による雰囲気作り、臨場感）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        ゲームの聴覚体験全般を評価します。音楽がゲームの雰囲気を盛り上げているか、効果音がアクションにメリハリを与えているか、音声が物語やキャラクターを魅力的に演出しているかを総合的に判断してください。
                      </Typography>
                      
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        ゲームプレイ
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・コアメカニクス（戦闘、パズルなど、そのゲームの核となるシステム）
・ゲームバランス（難易度調整、成長システム、進行速度の適切さ）
・操作性（レスポンス、直感性、カスタマイズ性）
・UI/UX（メニューの使いやすさ、情報の見やすさ）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        「遊び」の核心部分を評価します。そのゲームの基本的な遊び方が楽しいか、操作していて気持ちいいか、適切な挑戦レベルが提供されているかを判断してください。インターフェースの使いやすさも含めて、プレイ体験全体の快適さを評価対象とします。
                      </Typography>
                      
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        ナラティブ（物語・演出）
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・ストーリー（物語の構成、展開、脚本）
・キャラクター（魅力、成長、描写）
・世界観（ゲームの舞台や背景、設定の作り込み）
・演出（イベントシーン、環境ストーリーテリング）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        ゲームが提供する「体験」や「物語」を評価します。ストーリー性の薄いゲームであっても、世界観や演出、キャラクターの魅力は存在します。そのゲームがどんな体験を提供し、プレイヤーにどんな感情を抱かせるかを総合的に判断してください。
                      </Typography>
                      
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        オリジナリティ（独創性）
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・アイデアの新しさ（斬新なシステム、ユニークな発想）
・既存要素の組み合わせ（既知の要素の新しい活用法）
・ジャンルへの貢献（そのジャンルに新しい価値を提供したか）
・印象に残る特徴（他のゲームとの差別化要素）`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#dadada' }}>
                        そのゲームならではの特色や革新性を評価します。完全に新しいアイデアでなくても、既存の要素を上手く組み合わせて新しい体験を生み出していれば高く評価できます。「このゲームでしか味わえない」要素があるかどうかを考えてみてください。
                      </Typography>
                      
                      {/* <Divider sx={{ my: 3 }} />
                      
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <Typography variant="h6" sx={{ mb: 1.5, color: 'text.primary' }}>
                          時代性を考慮した評価について
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, color: '#dadada' }}>
                          ゲームの評価において、そのタイトルがリリースされた時代背景を理解することは非常に重要です。技術的制約、当時の業界標準、プラットフォームの特性、社会情勢などを踏まえて評価することで、より公正で建設的な判断が可能になります。
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#dadada' }}>
                          例えば、1990年代のゲームを現在の技術基準で評価するのではなく、当時の技術的限界の中でどれだけ革新的だったか、同世代の他作品と比較してどうだったかを考慮してください。また、現在では一般的になったシステムや表現手法も、それらの発展に貢献した作品には相応の評価を与えるべきです。
                        </Typography>
                         <Typography variant="body2" sx={{ color: '#dadada' }}>
                         ただし、時代背景に詳しくなくても心配する必要はありません。
                         まずは自分が感じたままの体験を大切にし、その上で発売年やプラットフォームなどの情報を参考程度に活用し、時代性を考慮してみる姿勢を。
                         現在の視点からの素直な感想も、そのゲームの価値を測る重要な指標の一つです。
                        </Typography>
                      </Box> */}
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ 
                display: 'grid', 
                gap: 0.5,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Visual
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreVisual ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreVisual', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{ flex: 1, ...getDetailScoreStyle(tempData.scoreVisual) }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreVisual)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreVisual)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Sound
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreSound ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreSound', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{ flex: 1, ...getDetailScoreStyle(tempData.scoreSound) }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreSound)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreSound)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Gameplay
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreGameplay ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreGameplay', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{ flex: 1, ...getDetailScoreStyle(tempData.scoreGameplay) }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreGameplay)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreGameplay)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Narrative
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreNarrative ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreNarrative', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{ flex: 1, ...getDetailScoreStyle(tempData.scoreNarrative) }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreNarrative)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreNarrative)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Originality
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreOriginality ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreOriginality', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{ flex: 1, ...getDetailScoreStyle(tempData.scoreOriginality) }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreOriginality)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreOriginality)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            mt: -2 
          }}>
            <Button
              color="primary"
              size="small"
              onClick={() => setPreviewOpen(true)}
              disabled={!tempData.reviewText}
              sx={{ 
                fontSize: { xs: "0.55rem", sm: "0.6rem" },
                minWidth: 'auto',
                p: '2px 8px'
              }}
            >
              プレビュー
            </Button>
            <Tips 
              title="テキストフォーマット" 
              content={reviewFormatTips}
              buttonComponent={
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    fontSize: { xs: "0.55rem", sm: "0.6rem" },
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  使用可能フォーマット
                </Typography>
              }
            />
          </Box>
          <Box sx={{ mt: 0.25, mb: 2 }}>
            <StyledTextField
              id="review"
              label="レビュー"
              value={tempData.reviewText || ''}
              onChange={(value) => {
                setTempData({ ...tempData, reviewText: value });
                setIsDialogDirty(true);
              }}
              multiline
              minRows={fullScreen ? 6 : 4}
              maxRows={12}
              placeholder="レビューを入力してください"
            />
          </Box>

          <Box sx={{ my: 2 }}>
            <Box sx={{ mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="プレイ開始日"
                  type="date"
                  value={tempData.playStartDate || ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    setTempData({ ...tempData, playStartDate: input || null });
                    setIsDialogDirty(true);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: "9999-12-31" }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <LifeStageSelector
                  birthDate={userBirthDate}
                  targetDate={releaseDate}
                  onSelect={(date) => {
                    setTempData({ ...tempData, playStartDate: date });
                    setIsDialogDirty(true);
                  }}
                  mode="start"
                />
              </Box>
              {!tempData.playStartDate && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, ml: 0.5, lineHeight: 1.2 }}
                >
                  ライフステージセレクターで特定の時期に相当する日付を簡単に設定できます。
                </Typography>
              )}
              {tempData.playStartDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <TextField
                    label="プレイ終了日"
                    type="date"
                    value={tempData.playEndDate || ''}
                    onChange={(e) => {
                      const input = e.target.value;
                      setTempData({ ...tempData, playEndDate: input || null });
                      setIsDialogDirty(true);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 
                      max: "9999-12-31",
                      min: tempData.playStartDate
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <LifeStageSelector
                    birthDate={userBirthDate}
                    targetDate={releaseDate}
                    onSelect={(date) => {
                      setTempData({ ...tempData, playEndDate: date });
                      setIsDialogDirty(true);
                    }}
                    mode="end"
                    startDate={tempData.playStartDate}
                  />
                </Box>
              )}
            </Box>
            
            <TextField
              margin="dense"
              id="playTime"
              label="プレイ時間"
              type="number"
              fullWidth
              value={tempData.playTime || ''}
              onChange={(e) => {
                const value = e.target.value;
                setTempData({ 
                  ...tempData, 
                  playTime: value === '' ? null : Number(value)  // 空文字列の場合はnullを設定
                });
                setIsDialogDirty(true);
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">時間</InputAdornment>,
              }}
              size="small"
            />
          </Box>

          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography>プラットフォーム</Typography>
              <Typography 
                color="text.secondary" 
                variant="body2"
                sx={{ ml: 1 }}
              >
                {tempData.platform.length}/2
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {platforms.map((platform) => (
                <Chip
                  key={platform.platform_id}
                  label={platform.name}
                  onClick={() => handlePlatformChange(platform.platform_id)}
                  color={tempData.platform.includes(platform.platform_id) ? 'primary' : 'default'}
                  size="small"
                  disabled={!tempData.platform.includes(platform.platform_id) && tempData.platform.length >= 2}
                />
              ))}
              
              {availablePlatforms
                .filter(p => 
                  !platforms.some(defaultP => defaultP.platform_id === p.platform_id) &&
                  tempData.platform.includes(p.platform_id)
                )
                .map((platform) => (
                  <Chip
                    key={platform.platform_id}
                    label={platform.name}
                    onDelete={() => handlePlatformChange(platform.platform_id)}
                    color="primary"
                    size="small"
                  />
                ))}
              
              <Chip
                icon={<AddIcon />}
                label="その他"
                onClick={handlePlatformMenuOpen}
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
                disabled={tempData.platform.length >= 2}
              />

              <Menu
                anchorEl={platformMenuAnchorEl}
                open={Boolean(platformMenuAnchorEl)}
                onClose={handlePlatformMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: 300,
                    width: '300px',
                  },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    リメイクやリマスター版・移植版などは別タイトルとしてデータベースに登録されている可能性があることにご留意ください。適切ではないプラットフォームの追加は他ユーザーの混乱を招く恐れがあります。
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="プラットフォームを検索"
                    value={platformSearch}
                    onChange={(e) => setPlatformSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                {filteredPlatforms.length === 0 ? (
                  <MenuItem disabled>
                    該当するプラットフォームがありません
                  </MenuItem>
                ) : (
                  filteredPlatforms.map((platform) => (
                    <MenuItem
                      key={platform.platform_id}
                      onClick={() => handlePlatformSelect(platform.platform_id)}
                      selected={tempData.platform.includes(platform.platform_id)}
                    >
                      {platform.name}
                    </MenuItem>
                  ))
                )}
              </Menu>
            </Box>
          </Box>

          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography>スコア補正</Typography>
              <Tips title="スコア補正について" content={reviewCorrectionTips} maxWidth="sm"/>
              <Typography 
                color="text.secondary" 
                variant="body2"
                sx={{ ml: 1 }}
              >
                {tempData.scoreTags.length}/3
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {scoreTags.map((tag) => (
                <Chip
                  key={tag.id}
                  icon={
                    tag.id === 9 || tag.id >= 30 ? (
                      <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                    ) : (
                      <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                    )
                  }
                  label={tag.label}
                  onClick={() => handleScoreTagsChange(tag.id)}
                  color={tempData.scoreTags.includes(tag.id) ? 'primary' : 'default'}
                  size="small"
                  disabled={!tempData.scoreTags.includes(tag.id) && tempData.scoreTags.length >= 3}
                  sx={{
                    '& .MuiChip-icon': { fontSize: '0.7rem' },
                    '& .MuiChip-label': { 
                      fontSize: '0.6rem',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 簡易メモ */}
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography>メモ</Typography>
              <Typography variant="caption" color="text.secondary">
                {memo.length}/200
              </Typography>
            </Box>
            <StyledTextField
              id="memo"
              label=""
              value={memo}
              onChange={(value) => setMemo(value)}
              multiline
              minRows={2}
              maxRows={4}
              placeholder="このゲームについての非公開メモ（200文字まで）"
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* クリアタイム */}
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography>クリアタイム</Typography>
              <Tips
                title="クリアタイムについて"
                content={
                  <Box>
                    <Typography variant="body2" paragraph>
                      <strong>メイン</strong>: メインストーリーや主目的をクリアするのに必要な時間。寄り道をしない。
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>メイン + サブ</strong>: メインストーリーに加えて主要なサブクエストやサイドコンテンツをクリアするのに必要な時間。
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>コンプリート</strong>: ゲーム内の全ての要素をクリアするのに必要な時間。
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>フリー</strong>: 特定のプレイスタイルにこだわらないクリアタイムを記録できます。プレイ内容の詳細を記入する必要があります。
                    </Typography>
                  </Box>
                }
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, lineHeight: 1.4 }}>
              記録したクリアタイムは、ゲームの平均クリアタイムの参考データとして、プレイスタイルごとに集計されます。
              ノートにプレイ内容を記述することができ、これは他のユーザーの参考にもなります。
              "フリー"を選択する場合は、ノートの入力が必須です。
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>プレイスタイル</InputLabel>
              <Select
                value={selectedPlaystyle}
                label="プレイスタイル"
                onChange={(e) => handlePlaystyleChange(e.target.value as number)}
                size="small"
              >
                {Object.entries(PLAYSTYLE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={parseInt(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="時間"
                type="number"
                value={playtimeHours}
                onChange={(e) => setPlaytimeHours(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <TextField
                label="分"
                type="number"
                value={playtimeMinutes}
                onChange={(e) => setPlaytimeMinutes(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{ inputProps: { min: 0, max: 59 } }}
              />
            </Box>

            <TextField
              label={selectedPlaystyle === 0 ? "ノート（必須）" : "ノート"}
              value={playtimeNote}
              onChange={(e) => setPlaytimeNote(e.target.value)}
              multiline
              minRows={2}
              maxRows={3}
              fullWidth
              size="small"
              placeholder={selectedPlaystyle === 0 ? "フリースタイルの場合、プレイ内容の詳細を必ず記入してください" : "プレイスタイルの詳細メモ"}
              required={selectedPlaystyle === 0}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {playtimeRecords.find(r => r.playstyle === selectedPlaystyle) ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSavePlaytime}
                    fullWidth
                  >
                    記録を編集
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDeletePlaytime}
                    color="error"
                    fullWidth
                  >
                    記録を削除
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSavePlaytime}
                  fullWidth
                >
                  クリアタイムを記録
                </Button>
              )}
            </Box>

            {playtimeRecords.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  登録済み記録
                </Typography>
                {playtimeRecords.map((record) => (
                  <Chip
                    key={record.playstyle}
                    label={`${PLAYSTYLE_LABELS[record.playstyle]}: ${Math.floor(record.play_time_minutes / 60)}h ${record.play_time_minutes % 60}m`}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} color="primary" variant="contained">保存</Button>
          <Button onClick={() => onClose()} color="inherit">キャンセル</Button>
        </DialogActions>
      </Dialog>

      <ReviewPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        reviewText={tempData.reviewText || ''}
      />
    </>
  );
};

export default GameReviewDialog;